import { useSearchParams } from 'react-router';
import { useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';

export const QUERY_SEARCH_PARAM_FILTER_SEARCH = 'f_search';
export const QUERY_SEARCH_PARAM_FILTER_ACTIONS = 'f_actions';

export function useArticleListSearchFilter() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [localSearchTerm, setLocalSearchTerm] = useState(
    () => searchParams.get(QUERY_SEARCH_PARAM_FILTER_SEARCH) ?? ''
  );

  const debouncedUpdateURL = useMemo(
    () =>
      debounce((term: string) => {
        const url = new URL(window.location.href);

        if (term.trim()) {
          url.searchParams.set(QUERY_SEARCH_PARAM_FILTER_SEARCH, term);
        } else {
          url.searchParams.delete(QUERY_SEARCH_PARAM_FILTER_SEARCH);
        }

        window.history.replaceState(window.history.state, '', url);
      }, 800),
    []
  );

  useEffect(() => {
    return () => {
      debouncedUpdateURL.cancel();
    };
  }, [debouncedUpdateURL]);

  const changeSearchTerm = useCallback(
    (term: string) => {
      setLocalSearchTerm(term);
      debouncedUpdateURL.cancel();
      debouncedUpdateURL(term);
    },
    [debouncedUpdateURL]
  );

  const setShowOnlyActions = useCallback(
    (show: boolean) => {
      setSearchParams(
        prev => {
          const newParams = new URLSearchParams(prev);

          if (show) {
            newParams.set(QUERY_SEARCH_PARAM_FILTER_ACTIONS, 'true');
          } else {
            newParams.delete(QUERY_SEARCH_PARAM_FILTER_ACTIONS);
          }

          return newParams;
        },
        { replace: true, preventScrollReset: true }
      );
    },
    [setSearchParams]
  );

  const deferredSearchTerm = useDeferredValue(localSearchTerm);
  const showOnlyActions = searchParams.get(QUERY_SEARCH_PARAM_FILTER_ACTIONS) === 'true';

  return {
    searchTerm: localSearchTerm,
    deferredSearchTerm,
    changeSearchTerm,
    showOnlyActions,
    setShowOnlyActions
  };
}
